import {
  Entry,
  Leave,
  LeaveEntity,
  LeaveEntityDateRange,
  ModifyAbsenceNewEntry,
  NewEntryTalentData,
} from "../../entriesAPI";
import { AbsenceListEntry } from "./AbsenceHoursList/AbsenceHoursList";
import { useCallback, useState } from "react";
import { useAppDispatch } from "../../../../app/hooks";
import {
  fetchEntriesByWrkentAddIds,
  postModifyAbsence,
  postResetEntryApprovalState,
} from "../../entriesSlice";
import LeaveMain from "./LeaveMain";
import { convertDateToYYYYMMDDString } from "../../../../app/common";
import { useConfirmationDialog } from "../../../../contexts/ConfirmationDialogContext";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  ABSENCE_TYPES_REQUIRING_ATTACHMENT,
  hasApprovedEntries,
  hasEntriesNeedingApprovalReset,
  toApprovalResetPayload,
} from "../Shared/functions";

interface Props {
  selectedEntry: Entry | null;
  closeModal: () => void;
  translatedEntryTypes: { [key: string]: string };
  leaveEntity: LeaveEntity;
  setLeaveEntity: (val: LeaveEntity) => void;
  updateLeaveEntity: (
    key: string,
    value: string | LeaveEntityDateRange | null | Leave,
  ) => void;
  clearLeaveEntity: () => void;
  newEntryTalentData?: NewEntryTalentData;
}

export {
  ABSENCE_TYPES_REQUIRING_ATTACHMENT,
  SICK_LEAVE_TYPES,
  CONTINUABLE_ABSENCE_TYPES,
  MIXABLE_ABSENCE_TYPES,
} from "../Shared/functions";

const Absences = ({
  selectedEntry,
  closeModal,
  translatedEntryTypes,
  leaveEntity,
  setLeaveEntity,
  updateLeaveEntity,
  clearLeaveEntity,
  newEntryTalentData,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { confirm } = useConfirmationDialog();

  const [absenceToEdit, setAbsenceToEdit] = useState<AbsenceListEntry | null>(
    null,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [originalLeaveEntity, setOriginalLeaveEntity] =
    useState<LeaveEntity | null>(leaveEntity);

  const handleConfirmSave = useCallback(
    async (
      markSelectedAsApproved: boolean,
      entries: AbsenceListEntry[],
      versionOverride?: number,
      isAddingAttachment?: boolean,
    ) => {
      const checkedEntries = entries.filter((e) => e.checked);
      const isAttachmentRequired = ABSENCE_TYPES_REQUIRING_ATTACHMENT.includes(
        leaveEntity.type,
      );
      const hasApprovedOrRejected =
        hasEntriesNeedingApprovalReset(checkedEntries);
      const hasApproved = hasApprovedEntries(checkedEntries);
      const leaveHasAttachment = (leaveEntity.leaveAttachment?.attachments?.length ?? 0) > 0;

      if (isAttachmentRequired && hasApprovedOrRejected && !isAddingAttachment && !markSelectedAsApproved && !leaveHasAttachment) {
        const ok = await confirm({
          title: t("hourEntries:resetApprovalDialogTitle"),
          contentText: (
            <Box>
              <Typography>
                {t("hourEntries:resetApprovalDialogText1")}
              </Typography>
              {hasApproved && (
                <Typography mt={1}>
                  {t("hourEntries:resetApprovalDialogText2")}
                </Typography>
              )}
            </Box>
          ),
          confirmText: t("common:confirm"),
          cancelText: t("common:cancelShort"),
        });
        if (!ok) return;

        setIsLoading(true);
        const entriesToReset = toApprovalResetPayload(checkedEntries);
        const resetAction = await dispatch(
          postResetEntryApprovalState(entriesToReset),
        );
        if (!isFulfilled(resetAction)) {
          setIsLoading(false);
          return;
        }
      }

      // If leaveEntity is set and it has start and end dates
      if (
        leaveEntity &&
        leaveEntity?.dateRange?.startDate &&
        leaveEntity?.dateRange?.endDate &&
        selectedEntry
      ) {
        setIsLoading(true);

        // Separate checked entries into new and existing based on version
        const newEntries: ModifyAbsenceNewEntry[] = entries
          .filter(
            (entry) =>
              entry.checked &&
              entry.version === 0 &&
              entry.assignmentId !== undefined,
          )
          .map((item) => ({
            assignmentId: item.assignmentId as string,
            customerCostCenterId: item.customerCostCenterId,
            approve: markSelectedAsApproved,
            start: item.start,
            end: item.end,
            isBillable: item?.isBillable,
            lunchBreak: item?.lunchBreak,
          }));

        const existingEntries = entries
          .filter((entry) => entry.checked && entry.version !== 0)
          .map((item) => ({
            entryId: item.id,
            approve: markSelectedAsApproved,
            start: item.start,
            end: item.end,
            isBillable: item?.isBillable,
            lunchBreak: item?.lunchBreak,
          }));

        // Adjust midnight date to previous day at 23:59:59 if the time is exactly 00:00:00
        const adjustMidnightDate = (timestamp: number): number => {
          const date = new Date(timestamp);
          if (
            date.getHours() === 0 &&
            date.getMinutes() === 0 &&
            date.getSeconds() === 0
          ) {
            // If the time is exactly midnight, adjust to 23:59:59 of the previous day
            date.setDate(date.getDate() - 1);
            date.setHours(23, 59, 59, 999); // Set to the last millisecond of the previous day
          }
          return date.getTime();
        };

        let startDate = null;
        let endDate = null;

        // Find start and end dates for leave, based on checked entries (existing or new)
        const allCheckedEntries = [...newEntries, ...existingEntries];
        if (allCheckedEntries.length > 0) {
          startDate = allCheckedEntries.reduce(
            (minDate, entry) => (entry.start < minDate ? entry.start : minDate),
            allCheckedEntries[0].start,
          );

          endDate = allCheckedEntries.reduce(
            (maxDate, entry) => (entry.end > maxDate ? entry.end : maxDate),
            allCheckedEntries[0].end,
          );
        }

        // Adjust the endDate if it's exactly midnight
        if (endDate) {
          endDate = adjustMidnightDate(endDate);
        }

        /* Find talentId for leave
        - newEntryTalentData should be there when creating new absence
        - leaveEntity should have talentId when editing existing absence
        - selectedEntry should have talentId when converting entry to absence
        - Currently undefined should not be possible, but BE considers it as editing existing absence (talentId can be omitted when editing existing absence)
        */
        const talentId =
          newEntryTalentData?.id ||
          leaveEntity?.talentId ||
          selectedEntry.talentId ||
          undefined;

        // Fallback for `startDate` and `endDate` if they are null or need to be in a specific format
        const formattedStartDate = startDate
          ? convertDateToYYYYMMDDString(new Date(startDate), "-")
          : leaveEntity?.dateRange?.startDate;

        const formattedEndDate = endDate
          ? convertDateToYYYYMMDDString(new Date(endDate), "-")
          : leaveEntity?.dateRange?.endDate;

        formattedStartDate &&
          formattedEndDate &&
          dispatch(
            postModifyAbsence({
              ...(leaveEntity?.id ? { absenceId: leaveEntity.id } : {}),
              ...(!leaveEntity?.id || newEntries.length > 0
                ? { talentId: talentId }
                : {}),
              ...(leaveEntity?.id
                ? { version: versionOverride ?? leaveEntity.version }
                : {}),
              type: leaveEntity.type,
              startDate: formattedStartDate,
              endDate: formattedEndDate,
              continuationOf: leaveEntity?.continuationOf?.id || null,
              entries: [...existingEntries],
              newEntries: [...newEntries],
            }),
          ).then((action) => {
            if (action.meta.requestStatus === "fulfilled") {
              // Find correct event from response
              const event = action.payload.find(
                (event: any) =>
                  event?.name === "absenceNew" || event?.name === "absenceChg",
              );

              if (event) {
                const updatedLeaveEntity = {
                  ...leaveEntity,
                  id: event.id,
                  version: event.version,
                };

                // Update leaveEntity with id and version
                if (event) {
                  setLeaveEntity({ ...updatedLeaveEntity });
                  setOriginalLeaveEntity({ ...updatedLeaveEntity });
                }

                /* Backend isnt able to provide enough data in wrkentAdd event,
                so we need to fetch the entry separately to update entries list.
                Look through events, if there's wrkEntAdd event */
                const workEntryAddEvents = action.payload.filter(
                  (event: any) => event?.name === "wrkentAdd",
                );

                if (workEntryAddEvents.length > 0) {
                  dispatch(fetchEntriesByWrkentAddIds(workEntryAddEvents));
                }
              } else {
                console.log("warning no event found");
              }
              setIsLoading(false);
              absenceToEdit && setAbsenceToEdit(null);
              closeModal();
            } else {
              setIsLoading(false);
            }
          });
      } else {
        setIsLoading(false);
        closeModal();
      }
    },
    [
      leaveEntity,
      selectedEntry,
      newEntryTalentData,
      dispatch,
      setLeaveEntity,
      absenceToEdit,
      closeModal,
      confirm,
      t,
    ],
  );

  return (
    <>
      {selectedEntry && (
        <LeaveMain
          selectedEntry={selectedEntry!}
          cancel={closeModal}
          leaveEntity={leaveEntity}
          setLeaveEntity={setLeaveEntity}
          updateLeaveEntity={updateLeaveEntity}
          originalLeaveEntity={originalLeaveEntity}
          setOriginalLeaveEntity={setOriginalLeaveEntity}
          clearLeaveEntity={clearLeaveEntity}
          translatedEntryTypes={translatedEntryTypes}
          handleConfirmSave={handleConfirmSave}
          absenceToEdit={absenceToEdit}
          setAbsenceToEdit={setAbsenceToEdit}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
          newEntryTalentData={newEntryTalentData}
        />
      )}
    </>
  );
};

export default Absences;
